import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react'
import posthog from 'posthog-js'
import * as ppWeb from 'pp-web'
import {
  clearPhDistinctId,
  getStoredPhDistinctId,
} from '../utils/utm'

// Check if PostHog is active
// PostHog is only active in qa/prod environments, not in local/dev
export function isPostHogActive() {
  const cfg = ppWeb.cfg
  const profile = cfg?.profile || 'local'
  // Only activate PostHog if API key exists AND profile is not 'local'
  return !!(cfg?.postHog?.apiKey && profile !== 'local')
}

// Check if PostHog is already initialized (from main.jsx)
function isPostHogInitialized() {
  // PostHog sets config property when initialized
  return typeof window !== 'undefined' && posthog.config && posthog.config.api_key
}

function PostHogPageView() {
  const location = useLocation()
  const posthogClient = usePostHog()
  const { user } = useAuth()

  // Track pageviews
  useEffect(() => {
    if (location.pathname && posthogClient && isPostHogActive()) {
      const url = window.location.origin + location.pathname + location.search
      posthogClient.capture('$pageview', { $current_url: url })
    }
  }, [location.pathname, location.search, posthogClient])

  const aliasedRef = useRef(false)

  // Identify user when authenticated; alias public-site anonymous id first when present.
  useEffect(() => {
    if (!user || !posthogClient || !isPostHogActive()) {
      return
    }

    const cognitoId = user.userId || user.sub || user.username
    if (!cognitoId) return

    if (!posthogClient._isIdentified()) {
      posthogClient.identify(cognitoId, {
        email: user.email,
        cognitoId: cognitoId,
        name: user.name || user.givenName || user.email?.split('@')[0],
      })
      const anonId = getStoredPhDistinctId()
      if (anonId && anonId !== cognitoId && !aliasedRef.current) {
        try {
          // Merge public-site anonymous person into this identified user.
          posthogClient.alias(anonId)
          aliasedRef.current = true
        } catch (err) {
          console.warn('PostHog alias failed:', err)
        }
      }
      // ph id no longer needed after alias; UTMs stay until signup createUser clears them.
      clearPhDistinctId()
    }
  }, [user, posthogClient])

  return null
}

export function PostHogProvider({ children }) {
  const cfg = ppWeb.cfg

  // PostHog should already be initialized synchronously in main.jsx before React renders
  // This useEffect is a fallback in case initialization didn't happen in main.jsx
  useEffect(() => {
    if (typeof window === 'undefined' || !isPostHogActive() || isPostHogInitialized()) {
      return
    }

    // Fallback initialization if PostHog wasn't initialized in main.jsx
    console.warn('⚠️ PostHog not initialized in main.jsx, initializing in provider (fallback)')
    const apiHost = cfg.postHog.reverseProxy || cfg.postHog.host || 'https://us.i.posthog.com'

    posthog.init(cfg.postHog.apiKey, {
      api_host: apiHost,
      person_profiles: 'identified_only',
      capture_pageview: false, // Disable automatic pageview capture, we capture manually
      capture_pageleave: true,
      autocapture: false
    })
  }, [cfg])

  // Don't initialize PostHog provider if not active (e.g., in local/dev)
  if (!isPostHogActive()) {
    return <>{children}</>
  }

  return (
    <PHProvider client={posthog}>
      <PostHogPageView />
      {children}
    </PHProvider>
  )
}
